import { useState, useEffect } from 'react';
import { HotelMap } from '../components/hotels/hotel-map';

interface Hotel {
  id: string;
  name: string;
  location: string;
  coordinates?: {
    latitude: number;
    longitude: number;
    address?: string;
  } | null;
  price?: {
    amount: number;
    currency: string;
  };
  starRating?: number;
  amenities?: string[];
}

export default function TestMapPage() {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredHotelId, setHoveredHotelId] = useState<string | null>(null);

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      const response = await fetch('http://localhost:3000/api/hotels/search/map', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          destination: 'Miami',
          checkin: '2025-11-15',
          checkout: '2025-11-17',
          guests: [{ adults: 2, children: [] }]
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Transform the backend response to match the expected structure
        const transformedHotels = data.data.hotels.map((hotel: any) => ({
          id: hotel.id,
          name: hotel.name,
          location: hotel.location?.address || hotel.location || 'Unknown',
          coordinates: hotel.location && hotel.location.latitude && hotel.location.longitude ? {
            latitude: hotel.location.latitude,
            longitude: hotel.location.longitude,
            address: hotel.location.address
          } : null,
          price: hotel.price,
          starRating: hotel.starRating || hotel.stars,
          amenities: hotel.amenities || []
        }));
        setHotels(transformedHotels);
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = (hotelId: string) => {
    console.log('Clicked hotel:', hotelId);
    
    // Scroll to hotel in list
    const hotelElement = document.getElementById(`hotel-${hotelId}`);
    if (hotelElement) {
      hotelElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      
      // Add temporary highlight
      hotelElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
      setTimeout(() => {
        hotelElement.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading hotels...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Test Map - Hotel Map View
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Hotel List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Hotels ({hotels.length})
            </h2>
            <div className="space-y-3">
              {hotels.map((hotel) => (
                <div
                  key={hotel.id}
                  id={`hotel-${hotel.id}`}
                  className={`p-4 bg-white rounded-lg border cursor-pointer transition-colors ${
                    hoveredHotelId === hotel.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onMouseEnter={() => setHoveredHotelId(hotel.id)}
                  onMouseLeave={() => setHoveredHotelId(null)}
                >
                  <h3 className="font-semibold text-gray-900">{hotel.name}</h3>
                  {hotel.price && (
                    <p className="text-green-600 font-medium">
                      ${Math.round(hotel.price.amount)} / night
                    </p>
                  )}
                  {hotel.coordinates?.address && (
                    <p className="text-sm text-gray-600">{hotel.coordinates.address}</p>
                  )}
                  {hotel.coordinates && (
                    <p className="text-xs text-gray-500">
                      📍 {hotel.coordinates.latitude.toFixed(4)}, {hotel.coordinates.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Map
            </h2>
            <HotelMap
              hotels={hotels}
              onMarkerClick={handleMarkerClick}
              hoveredHotelId={hoveredHotelId}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
